import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { 
  Search, ChevronLeft, ChevronRight, MoreHorizontal, Eye, Ban, 
  AlertTriangle, Shield, UserCheck, Clock
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin`;

const AdminUsers = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [photoFilter, setPhotoFilter] = useState('all');

  useEffect(() => {
    fetchUsers();
  }, [page, statusFilter, photoFilter]);

  const fetchUsers = async (searchQuery = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '20'
      });
      
      if (searchQuery) params.append('search', searchQuery);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      if (photoFilter === 'with') params.append('has_photo', 'true');
      if (photoFilter === 'without') params.append('has_photo', 'false');

      const response = await axios.get(`${API}/users?${params.toString()}`);
      setUsers(response.data.users);
      setTotalPages(response.data.pages);
      setTotal(response.data.total);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    fetchUsers(search);
  };

  const handleQuickAction = async (userId, action) => {
    try {
      if (action === 'suspend') {
        await axios.post(`${API}/users/${userId}/suspend`, {
          user_id: userId,
          reason: 'Quick suspension from admin panel',
          duration_days: 7
        });
        toast.success('User suspended for 7 days');
      } else if (action === 'ban') {
        await axios.post(`${API}/users/${userId}/ban`, {
          user_id: userId,
          reason: 'Banned from admin panel'
        });
        toast.success('User banned');
      } else if (action === 'verify') {
        await axios.post(`${API}/users/${userId}/verify`);
        toast.success('User verified');
      }
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Action failed');
    }
  };

  const getStatusBadge = (user) => {
    const status = user.account_status || 'active';
    const styles = {
      active: 'bg-green-500/10 text-green-500',
      suspended: 'bg-yellow-500/10 text-yellow-500',
      banned: 'bg-red-500/10 text-red-500',
      warned: 'bg-orange-500/10 text-orange-500'
    };
    return (
      <span className={`px-2 py-1 rounded text-xs font-medium ${styles[status] || styles.active}`}>
        {status}
      </span>
    );
  };

  const getPhotoUrl = (user) => {
    if (user?.public_photos?.[0]) {
      const photo = user.public_photos[0];
      if (photo.startsWith('data:') || photo.startsWith('http')) return photo;
      return `data:image/jpeg;base64,${photo}`;
    }
    return 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=100';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Users</h1>
          <p className="text-[#A1A1AA]">{total} total users</p>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4">
        <form onSubmit={handleSearch} className="flex-1 min-w-[200px]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#52525B]" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by username, email, or display name..."
              className="pl-10 bg-[#111] border-white/10 text-white"
            />
          </div>
        </form>

        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 bg-[#111] border-white/10">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-[#111] border-white/10">
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="suspended">Suspended</SelectItem>
            <SelectItem value="banned">Banned</SelectItem>
          </SelectContent>
        </Select>

        <Select value={photoFilter} onValueChange={(v) => { setPhotoFilter(v); setPage(1); }}>
          <SelectTrigger className="w-40 bg-[#111] border-white/10">
            <SelectValue placeholder="Photos" />
          </SelectTrigger>
          <SelectContent className="bg-[#111] border-white/10">
            <SelectItem value="all">All Users</SelectItem>
            <SelectItem value="with">With Photos</SelectItem>
            <SelectItem value="without">No Photos</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Users Table */}
      <div className="bg-[#111] border border-white/10 rounded-xl overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-[#FF2E63] border-t-transparent rounded-full animate-spin" />
          </div>
        ) : users.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-[#A1A1AA]">No users found</p>
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-white/5">
              <tr>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">User</th>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Email</th>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Status</th>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Joined</th>
                <th className="text-left p-4 text-xs font-medium text-[#A1A1AA] uppercase">Last Active</th>
                <th className="text-right p-4 text-xs font-medium text-[#A1A1AA] uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5">
              {users.map((user) => (
                <tr key={user.id} className="hover:bg-white/5">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={getPhotoUrl(user)}
                        alt=""
                        className="w-10 h-10 rounded-full object-cover cursor-pointer hover:ring-2 hover:ring-[#FF2E63] transition-all"
                        onClick={() => navigate(`/admin/users/${user.id}`)}
                        data-testid={`user-avatar-${user.id}`}
                      />
                      <div>
                        <div className="flex items-center gap-2">
                          <p 
                            className="text-white font-medium cursor-pointer hover:text-[#FF2E63] transition-colors"
                            onClick={() => navigate(`/admin/users/${user.id}`)}
                          >
                            {user.display_name || user.username}
                          </p>
                          {user.is_verified && (
                            <Shield className="w-4 h-4 text-[#08D9D6]" />
                          )}
                          {user.is_online && (
                            <span className="w-2 h-2 rounded-full bg-green-500" />
                          )}
                        </div>
                        <p className="text-sm text-[#52525B]">@{user.username}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-4 text-[#A1A1AA]">{user.email}</td>
                  <td className="p-4">{getStatusBadge(user)}</td>
                  <td className="p-4 text-[#A1A1AA] text-sm">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="p-4 text-[#52525B] text-sm">
                    {user.last_active ? new Date(user.last_active).toLocaleDateString() : 'Never'}
                  </td>
                  <td className="p-4 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="text-[#A1A1AA] hover:text-white">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-[#111] border-white/10">
                        <DropdownMenuItem 
                          onClick={() => navigate(`/admin/users/${user.id}`)}
                          className="text-[#EDEDED]"
                        >
                          <Eye className="w-4 h-4 mr-2" />
                          View Details
                        </DropdownMenuItem>
                        {!user.is_verified && (
                          <DropdownMenuItem 
                            onClick={() => handleQuickAction(user.id, 'verify')}
                            className="text-[#08D9D6]"
                          >
                            <UserCheck className="w-4 h-4 mr-2" />
                            Verify User
                          </DropdownMenuItem>
                        )}
                        {user.account_status !== 'suspended' && user.account_status !== 'banned' && (
                          <DropdownMenuItem 
                            onClick={() => handleQuickAction(user.id, 'suspend')}
                            className="text-yellow-500"
                          >
                            <Clock className="w-4 h-4 mr-2" />
                            Suspend 7 Days
                          </DropdownMenuItem>
                        )}
                        {user.account_status !== 'banned' && (
                          <DropdownMenuItem 
                            onClick={() => handleQuickAction(user.id, 'ban')}
                            className="text-red-500"
                          >
                            <Ban className="w-4 h-4 mr-2" />
                            Ban User
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4 border-t border-white/10">
            <p className="text-sm text-[#A1A1AA]">
              Page {page} of {totalPages}
            </p>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="text-[#A1A1AA]"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Previous
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="text-[#A1A1AA]"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
